import { Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { StatusChip } from '@/components/ui/status-chip';
import { NewSimulationDialog } from '@/features/market-maker/simulation-editor/new-simulation-dialog';
import { MARKET_MAKER_SIMULATIONS_QUERY_KEY, useCancelSimulation } from '@/hooks/queries/market-maker/use-market-maker';
import type { SimulationEntry, SimulationsResponse } from '@/services/api/market-maker';
import { formatDate } from '@/utils/format';
import { PURPLE_CHIP_CLASS } from './status';

interface SimulationCardProps {
  market: string;
  simulation: SimulationEntry;
  onCancel: () => void;
}

const SimulationCard = ({ market, simulation, onCancel }: SimulationCardProps) => (
  <Card className="flex h-full flex-col">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-base font-semibold">{market}</CardTitle>
      <StatusChip className={`px-2 py-0.5 text-xs ${PURPLE_CHIP_CLASS}`}>{simulation.role}</StatusChip>
    </CardHeader>
    <CardContent className="flex flex-1 flex-col gap-2">
      <div className="text-sm">
        Scenario: <span className="font-bold">{simulation.scenario}</span>
      </div>
      {simulation.estimatedStartTime ? (
        // estimatedEndTime may be undefined → formatDate renders 'not set'
        <div className="text-sm text-muted-foreground">
          {formatDate(simulation.estimatedStartTime)} → {formatDate(simulation.estimatedEndTime)}
        </div>
      ) : null}
      <div className="mt-auto pt-1">
        <Button className="gap-1.5 text-red-500" onClick={onCancel} size="sm" type="button" variant="ghost">
          <X className="size-3.5" />
          Cancel
        </Button>
      </div>
    </CardContent>
  </Card>
);
SimulationCard.displayName = 'SimulationCard';

interface SimulationsSectionProps {
  simulations: SimulationsResponse;
  /** Market ids for the New Simulation dialog's driver/follower selects */
  markets: string[];
}

// Record keyed by market — a multi-market scenario (driver + followers)
// produces several cards sharing one scenario string. Insertion order kept (no sort).
export const SimulationsSection = ({ simulations, markets }: SimulationsSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const cancelMutation = useCancelSimulation();

  const entries = Object.entries(simulations);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Simulations</h2>
        <Button className="gap-1.5" onClick={() => setIsDialogOpen(true)} size="sm" type="button" variant="outline">
          <Plus className="size-3.5" />
          New Simulation
        </Button>
      </div>
      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">No active simulations</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {entries.map(([market, simulation]) => (
            // Cancel by market only — cancelling a market cancels that market's
            // participation; scenario is never sent (legacy behaviour)
            <SimulationCard key={market} market={market} onCancel={() => cancelMutation.mutate({ market })} simulation={simulation} />
          ))}
        </div>
      )}
      <NewSimulationDialog
        markets={markets}
        onOpenChange={setIsDialogOpen}
        onSubmitted={() => queryClient.invalidateQueries({ queryKey: MARKET_MAKER_SIMULATIONS_QUERY_KEY })}
        open={isDialogOpen}
      />
    </section>
  );
};
SimulationsSection.displayName = 'SimulationsSection';
